import moment from "moment/moment";
import { encode } from "js-base64";
import React, { useState, useMemo } from "react";
import { Divider, Skeleton } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { IRURL, RWURL } from "../../Config/Api-constants";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CustomTablePagination from "./CustomTablePagination";
import { SortIcon, PreviewIcon } from "../../../Assets/Svg/Svg";
import { BorderLinearProgress } from "../../Common/MuiStyles/MuiStyles";
import { Menu, MenuItem, ListItemIcon, TextField, InputAdornment, ListItemText, Table, Tooltip, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Avatar, Typography, Box, Chip, Button, IconButton } from "@mui/material";
import DateFilter from "./DateFilter";
import StatusFilter from "./StatusFilter";
import { LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledProgressBar = styled(LinearProgress)(({ theme }) => ({
    height: 30,
    borderRadius: 6,
    backgroundColor: '#F1F3F4', // light background
    '& .MuiLinearProgress-bar': {
        backgroundColor: '#4282E2', // light blue bar
        borderRadius: 6,
        opacity: 0.14
    },
}));

const headers = [
    { key: "order", label: "ORDER# AND ADDRESS" },
    { key: "templates", label: "TEMPLATES" },
    { key: "inspectors", label: "ASSIGNED INSPECTORS" },
    { key: "lastModified", label: "LAST MODIFIED" },
    { key: "status", label: "STATUS" },
];

const statusColors = {
    "completed": { primary: "#00644A", secondary: "#C7F6E0" },
    "scheduled": { primary: "#1F2F9C", secondary: "#C7DBF6" },
    "unscheduled": { primary: "#B71C1C", secondary: "#FFCDD2" },
    "NOT STARTED": { primary: "#1F2F9C", secondary: "#C7DBF6" },
    "IN PROGRESS": { primary: "#CB8530", secondary: "#FFF9C8" },
    "MARKED COMPLETE": { primary: "#00644A", secondary: "#C7F6E0" },
};

const IR_URL = await IRURL();
const RW_URL = await RWURL();
const associateCompany = sessionStorage.getItem("associate_company");

const ReportTable = ({ data, sortDirection, setSortDirection, sortBy, setSortBy, setFromDate, selectedDate, setSelectedDate, changeStatus, clearAllFilters, setToDate, search, setPage, status, setStatus, setInputText, loading, page, rowsPerPage, totalCount, onPageChange, onRowsPerPageChange }) => {
    // const [sortBy, setSortBy] = useState();
    const [anchorEl, setAnchorEl] = useState(null);
    // const [sortDirection, setSortDirection] = useState("asc");
    const [selectedOrder, setSelectedOrder] = useState(null);
    console.log(data,"data")
    const open = Boolean(anchorEl);

    const handleClick = (event, order) => {
        setAnchorEl(event.currentTarget);
        setSelectedOrder(order);
        console.log(order, "selectedOrder")
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const sortFieldMap = {
        order: 'location_address',
        templates: 'template_name',
        inspectors: 'inspector_name',
        lastModified: 'updated_at',
        status: 'status',
    };

    const handleSort = (key) => {
        const apiField = sortFieldMap[key] || 'updated_at';

        if (sortBy === apiField) {
            setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(apiField); // Use backend field name
            setSortDirection("asc");
        }
        setPage(0); // Reset pagination
    };


    const sortedOrders = useMemo(() => {
        if (!data) return [];
        return [...data].sort((a, b) => {
            let valA, valB;

            const getEffectiveDate = (order) =>
                new Date(order.updatedAt || order.createdAt || "1900-01-01");

            // Default sorting: latest updatedAt or createdAt (DESC)
            if (!sortBy) {
                const dateA = getEffectiveDate(a);
                const dateB = getEffectiveDate(b);
                return dateB - dateA; // Newest first
            }

            // If sortBy is 'orderStartDate', use scheduled_dte
            if (sortBy === 'orderStartDate') {
                const dateA = new Date(a.scheduled_dte || "1900-01-01");
                const dateB = new Date(b.scheduled_dte || "1900-01-01");
                const dateCompare = dateB - dateA;
                return sortDirection === "asc" ? -dateCompare : dateCompare;
            }

            // Sort by selected field
            switch (sortBy) {
                case 'order':
                    valA = a.address1?.toLowerCase() || '';
                    valB = b.address1?.toLowerCase() || '';
                    break;
                case 'templates':
                    valA = a.inspection_templates?.map(t => t.name).join(", ").toLowerCase() || '';
                    valB = b.inspection_templates?.map(t => t.name).join(", ").toLowerCase() || '';
                    break;
                case 'inspectors':
                    valA = a.inspectors?.map(i => i.name).join(", ").toLowerCase() || '';
                    valB = b.inspectors?.map(i => i.name).join(", ").toLowerCase() || '';
                    break;
                case 'lastModified':
                    valA = new Date(a.updatedAt || a.createdAt || "1900-01-01");
                    valB = new Date(b.updatedAt || b.createdAt || "1900-01-01");
                    break;
                case 'status':
                    valA = a.status?.toLowerCase() || '';
                    valB = b.status?.toLowerCase() || '';
                    break;
                default:
                    // Fallback: updatedAt or createdAt
                    const fallbackDateA = getEffectiveDate(a);
                    const fallbackDateB = getEffectiveDate(b);
                    return fallbackDateB - fallbackDateA;
            }

            // Compare values for string/date fields
            if (valA < valB) return sortDirection === "asc" ? -1 : 1;
            if (valA > valB) return sortDirection === "asc" ? 1 : -1;

            // If equal, fallback to latest updatedAt/createdAt
            const fallbackDateA = getEffectiveDate(a);
            const fallbackDateB = getEffectiveDate(b);
            return fallbackDateB - fallbackDateA;
        });
    }, [data, sortBy, sortDirection]);


    // const formatDate = (dateStr) => {
    //     if (!dateStr) return "--";
    //     const date = new Date(dateStr);
    //     return isNaN(date.getTime())
    //         ? "--"
    //         : date.toLocaleDateString(undefined, {
    //             year: "numeric",
    //             month: "long",
    //             day: "numeric",
    //         });
    // };

    const formatAddress = (order) => {
        const cityStateZip = [
            order.city,
            order.state,
            order.zip
        ].filter(Boolean).join(' '); // joins only non-null/defined values with spaces

        const parts = [
            order.address1,
            order.address2,
            cityStateZip
        ].filter(Boolean); // removes any null, undefined, or ""

        return parts.join(', '); // joins remaining parts with commas
    };

    return (
        <>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        boxShadow: '0px 2px 5px 0px rgba(0, 0, 0, 0.08)',
                    },
                }}
            >
                {/* will require later */}
                <MenuItem onClick={() => { handleClose(); window.open("https://4isn.com" + selectedOrder?.order_url, '_blank'); }}>
                    <ListItemIcon>
                        <OpenInNewIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{
                        className: 'vert-icon-text',
                    }}>View inspection details</ListItemText>
                </MenuItem>
                {selectedOrder?.status !== "NOT STARTED" && <MenuItem className="vert-icon-text" onClick={() => {
                    window.open(`${IR_URL}?guid=${selectedOrder?.orderGuid}`, '_blank');
                    handleClose();
                }}>
                    <ListItemIcon >
                        <PreviewIcon />
                    </ListItemIcon>
                    <ListItemText primaryTypographyProps={{
                        className: 'vert-icon-text',
                    }}>Preview report</ListItemText>
                </MenuItem>}
            </Menu>
            {/* <div style={{ height: "57px", padding: "8px", backgroundColor: "red" }}>
            </div> */}
            <div style={{ height: "57px", display: "flex", padding: "8px", justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: "10px" }}>
                    {/* will require later */}
                    {/* <DateFilter selectedDate={selectedDate} setSelectedDate={setSelectedDate} setFromDate={setFromDate} setToDate={setToDate} setPage={setPage} /> */}
                    <StatusFilter status={status} setStatus={setStatus} setPage={setPage} />
                </div>
                <div>
                    <TextField
                        placeholder="Search"
                        variant="outlined"
                        size="small"
                        value={search}
                        onChange={(e) => setInputText(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: "#3b3b3b", fontSize: "20px" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: "280px",
                            // height: "30px",
                            // float: "right",
                            // mt: -"20px",
                            backgroundColor: '#fff',
                            fontFamily: "Open Sans",
                            borderRadius: '4px',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                                '& fieldset': {
                                    borderColor: '#D5D5D5',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#D5D5D5',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#D5D5D5',
                                },
                            },
                        }}
                    />
                </div>
            </div>
            <Divider />
            <TableContainer component={Paper} elevation={0} sx={{ borderBottomLeftRadius: 8, borderBottomRightRadius: 8, maxHeight: "75vh" }}>
                {!loading && data.length === 0 ? (
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)", // ✅ true centering
                            textAlign: "center",
                        }}
                    >
                        <Typography sx={{ fontStyle: "italic", color: "#666", mb: 1 }}>
                            No matching reports found.
                        </Typography>
                        <Typography sx={{ color: "#999", mb: 2 }}>
                            Try adjusting your filters or search terms.
                        </Typography>
                        <Button
                            onClick={clearAllFilters}
                            variant="text"
                            sx={{
                                fontWeight: "bold",
                                textTransform: "uppercase",
                                color: "#3b3b3b",
                                '&:hover': {
                                    textDecoration: 'underline',
                                    backgroundColor: 'transparent',
                                },
                            }}
                        >
                            Clear all filters
                        </Button>
                    </Box>
                ) : (
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{ borderBottom: "2px solid var(--color-neutral-neutral-300, #D5D5D5)" }}>
                                {headers?.map((header) => (
                                    <TableCell className="RSP_table_header" key={header.key} onClick={() => handleSort(header.key)} sx={{ cursor: "pointer", userSelect: "none", verticalAlign: 'top', backgroundColor: '#fff', zIndex: 3 }}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <SortIcon />
                                            {header.label}
                                        </Box>
                                    </TableCell>
                                ))}
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        {!loading && data.length === 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '60vh', // 🔥 Ensures vertical centering
                                    textAlign: 'center',
                                    width: '100%',
                                    fontSize: "16px",
                                    fontStyle: 'italic',
                                    fontFamily: "Source sans pro"
                                }}
                            >
                                <Box sx={{ fontStyle: 'italic', color: '#666', mb: 0.5 }}>
                                    No matching reports found.
                                </Box>
                                <Box sx={{ fontStyle: 'italic', color: '#999', mb: 2 }}>
                                    Try adjusting your filters or search terms.
                                </Box>
                                <Button
                                    onClick={clearAllFilters}
                                    variant="text"
                                    sx={{
                                        fontWeight: 'bold',
                                        textTransform: 'uppercase',
                                        color: '#3b3b3b',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    Clear all filters
                                </Button>
                            </Box>
                        )}
                        <TableBody>
                            {loading ? (
                                [...Array(8)].map((_, i) => (
                                    <TableRow key={`skeleton-${i}`}>
                                        <TableCell sx={{ padding: "16px 24px", width: "420px", minWidth: "370px" }}>
                                            <Box display="flex">
                                                <Skeleton variant="rounded" width={60} height={60} sx={{ mr: 2 }} />
                                                <Box flex={1}>
                                                    <Skeleton width="50%" height={24} />
                                                    <Skeleton width="80%" height={20} />
                                                    <Skeleton width="30%" height={18} />
                                                    <Box mt={1}>
                                                        <Skeleton width="100%" height={10} />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top', p: "20px 40px", width: "250px" }}>
                                            <Skeleton height={20} width="60%" />
                                            <Skeleton height={20} width="50%" />
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top', p: "20px 40px", width: "203px" }}>
                                            <Skeleton height={20} width="40%" />
                                            <Skeleton height={20} width="60%" />
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top', p: "20px 40px", width: "160px" }}>
                                            <Skeleton height={20} width="80%" />
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top', p: "20px 30px" }}>
                                            <Skeleton variant="rounded" width={80} height={24} />
                                        </TableCell>
                                        <TableCell align="right" sx={{ verticalAlign: 'top', pt: "20px" }}>
                                            <Skeleton variant="rectangular" width={120} height={32} />
                                        </TableCell>
                                    </TableRow>
                                ))) : data?.map((order, index) => {
                                    // const currentDate = formatDate(order.scheduled_dte);
                                    // const prevDate = index > 0 ? formatDate(sortedOrders[index - 1].scheduled_dte) : null;
                                    // const showDateHeader = currentDate !== prevDate;
                                    return (
                                        <React.Fragment key={`${order.id}-${index}`}>
                                            {/* will be implement later */}
                                            {/* {showDateHeader && (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ padding: "8px 16px" }}>
                                                <Typography className="RSP_table_date">
                                                    {currentDate}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )} */}
                                            <TableRow>
                                                <TableCell sx={{ verticalAlign: "top", padding: "0px 24px", width: "420px", minWidth: "370px" }}>
                                                    <Box display="flex" sx={{ p: "8px" }}>
                                                        <Avatar
                                                            variant="rounded"
                                                            src={order.cover_photo}
                                                            sx={{ width: 60, height: 60, mr: 2 }}
                                                        />
                                                        <Box flex={1}>
                                                            <Typography className="RSP_table_col_order">Order #{order.orderId}</Typography>
                                                            <Typography className="RSP_table_col_address">{formatAddress(order)}</Typography>
                                                            {([order.clientName, ...order.clients?.map(c => c.name) || []].filter(Boolean).length > 0) && (
                                                                <Typography className="RSP_table_col_client">
                                                                    Client: {[order.clientName, ...order.clients?.map(c => c.name) || []].filter(Boolean).join(", ")}
                                                                </Typography>
                                                            )}
                                                            {/* will be available in Phase 3 */}
                                                            <Box width="100%" sx={{ mt: 1 }}>
                                                                <div className="is__relative">
                                                                    <Typography>Total Progress</Typography>
                                                                    <Typography>
                                                                        {order.completed_line_input_controls}/{order.incomplete_line_input_controls}
                                                                    </Typography>
                                                                    {/* <BorderLinearProgress
                                                                    variant="determinate"
                                                                    value={
                                                                        order.total_line_input_controls
                                                                            ? (order.completed_line_input_controls / order.incomplete_line_input_controls) * 100
                                                                            : 0
                                                                    }
                                                                    category={{ color: '#2196F3' }}

                                                                /> */}
                                                                    <StyledProgressBar
                                                                        variant="determinate"
                                                                        value={
                                                                            order.total_line_input_controls
                                                                                ? (order.completed_line_input_controls / order.incomplete_line_input_controls) * 100
                                                                                : 0
                                                                        }
                                                                    />
                                                                </div>
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', p: "8px 0px 0px 40px", width: "250px", cursor: 'default' }}>
                                                    {order.inspection_templates.length > 0 ? (
                                                        order.inspection_templates.map((t, i) => {
                                                            const name = t?.name || '';
                                                            const shouldTruncate = name.length > 35;
                                                            return (
                                                                <Tooltip
                                                                    key={i}
                                                                    title={shouldTruncate ? name : ''}
                                                                    arrow
                                                                    componentsProps={{
                                                                        tooltip: {
                                                                            sx: {
                                                                                backgroundColor: 'black',
                                                                                color: 'white',
                                                                                fontFamily: '"Source Sans Pro", sans-serif',
                                                                                fontSize: '14px',
                                                                                px: 1.5,
                                                                                py: 1,
                                                                                '& .MuiTooltip-arrow': {
                                                                                    color: 'black', // Sets arrow color
                                                                                },
                                                                            },
                                                                        },
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontFamily: '"Source Sans Pro", sans-serif',
                                                                            color: 'text.primary',
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            maxWidth: '200px',
                                                                            display: 'block',
                                                                        }}
                                                                    >
                                                                        {name}
                                                                    </Typography>
                                                                </Tooltip>
                                                            );
                                                        })
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No template selected
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', p: "8px 0px 0px 40px", width: "203px" }}>
                                                    {order.inspectors.map((inspector, i) => (
                                                        <Typography key={i} variant="body2">{inspector.name}</Typography>
                                                    ))}
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', p: "8px 0px 0px 40px", width: "160px" }}>
                                                    <Typography variant="body2">
                                                        {order.updatedAt ? moment(moment.utc(order.updatedAt).local()).format('L') : moment(order.createdAt).format('L')}
                                                        <br />
                                                        {order.updatedAt ? moment(moment.utc(order.updatedAt).local()).format('LT') : moment(order.createdAt).format('LT')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', p: "10px 0px 0px 30px", width: "150px" }}>
                                                    <Chip
                                                        label={order.status}
                                                        size="small"
                                                        className="status_chip"
                                                        sx={{ color: statusColors[order.status]?.primary, backgroundColor: statusColors[order.status]?.secondary }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right" sx={{ verticalAlign: 'top', pt: "10px", minWidth: "230px" }}>
                                                    <Box display="flex" alignItems="flex-start" justifyContent="flex-end" gap={1}>
                                                        <Button startIcon={<ModeEditIcon />} className="edit_button"
                                                            onClick={() => {
                                                                console.log(order, "here")
                                                                if (order.status === 'NOT STARTED' && associateCompany !== "ISN") {
                                                                    changeStatus(order)
                                                                }
                                                                window.open(`${RW_URL}?q=${encode(`guid=${order.orderGuid}&company_key=${sessionStorage.getItem("company_key")}&associate_company=${sessionStorage.getItem("associate_company")}&secret_key=${sessionStorage.getItem("secret_key")}&user_id=${sessionStorage.getItem("user_id")}&user_int_id=${sessionStorage.getItem("user_int_id")}&username=${sessionStorage.getItem("username")}`)}`, "_blank");
                                                            }}
                                                        >Edit Report</Button>
                                                        {/* will require later */}
                                                        <div className="dots_background">
                                                            <IconButton size="small" onClick={(e) => handleClick(e, order)}><MoreVertIcon /></IconButton>
                                                        </div>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })}
                        </TableBody>
                        <div style={{ height: "13px" }}></div>
                    </Table>
                )}
                <CustomTablePagination count={totalCount}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange} />
            </TableContainer>
        </>
    );
};

export default ReportTable;